import { Switch } from '@thanx/uikit/switch'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import PopoverMenu from 'components/PopoverMenu'
import useFlag from 'hooks/useFlag'
import { usePermissionReason } from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import { Fields as Profile } from 'models/UserProfile'
import React, { useState } from 'react'
import { Button } from 'react-bootstrap-five'
import { useSelector } from 'react-redux'
import { selectTiers } from 'selectors/tier'
import BlockUserModal from './BlockUserModal'
import ClawbackPointsModal from './ClawbackPointsModal'
import GrantPurchaseModal from './GrantPurchaseModal'
import GrantTierModal from './GrantTierModal'
import useProfilePermissions from './useProfilePermissions'

type Props = {
  profile: Profile
}

const t = buildTranslate('users.view.actions')

const ActionsMenu: React.FC<Props> = ({ profile }) => {
  const [showBlockUserModal, setShowBlockUserModal] = useState(false)
  const [showGrantPurchaseModal, setShowGrantPurchaseModal] = useState(false)
  const [showClawbackPointsModal, setShowClawbackPointsModal] = useState(false)
  const [isGrantTierModalOpen, setIsGrantTierModalOpen] = useState(false)
  const enableClawbackPoints = useFlag('mx-clawback-points', false)
  // Edit tier lists the merchant's tiers; Grant purchase needs a location.
  const { canAccessTiers, canAccessLocations } = useProfilePermissions()
  const cannotAccessTiers = !canAccessTiers
  const cannotAccessLocations = !canAccessLocations
  const tiersReason = usePermissionReason('TiersProgram', cannotAccessTiers)
  const locationsReason = usePermissionReason(
    'Locations',
    cannotAccessLocations
  )

  const tiers = useSelector(selectTiers)
  // LOY-257: only offer Edit tier when the merchant has a tiers program. With
  // access, the loaded tiers list tells us; without it /tiers 403s, so a
  // customer tier is the only proof (same signal TierBadge uses for its lock).
  const merchantHasTiers = tiers.length > 0
  const profileHasTier = !!profile.current_tier
  const showEditTier = canAccessTiers ? merchantHasTiers : profileHasTier

  const canDisable = ['enrolled', 'favorited'].some(
    state => profile.membership_state === state
  )

  return (
    <>
      <PopoverMenu dismissOnClickOutside closeOnClick>
        <Switch condition={showEditTier}>
          <PermissionTooltip
            disabled={cannotAccessTiers}
            reason={tiersReason}
            id="edit-tier-perm"
            srOnlyReason
          >
            <Button
              className="d-block grey-90 pr-m"
              variant="link"
              disabled={cannotAccessTiers}
              onClick={() => setIsGrantTierModalOpen(true)}
            >
              {t('edit_tier')}
            </Button>
          </PermissionTooltip>
        </Switch>
        <PermissionTooltip
          disabled={cannotAccessLocations}
          reason={locationsReason}
          id="grant-purchase-perm"
          srOnlyReason
        >
          <Button
            className="d-block grey-90 pr-m"
            variant="link"
            disabled={cannotAccessLocations}
            onClick={() => setShowGrantPurchaseModal(true)}
          >
            {t('grant_purchase')}
          </Button>
        </PermissionTooltip>
        <Switch condition={enableClawbackPoints}>
          <Button
            className="d-block grey-90 pr-m"
            variant="link"
            onClick={() => setShowClawbackPointsModal(true)}
          >
            {t('remove_points')}
          </Button>
        </Switch>
        <Button
          className="d-block grey-90 pr-m"
          variant="link"
          disabled={!canDisable}
          onClick={() => setShowBlockUserModal(true)}
        >
          {t('block_user')}
        </Button>
      </PopoverMenu>
      <BlockUserModal
        profile={profile}
        isOpen={showBlockUserModal}
        onClose={() => setShowBlockUserModal(false)}
      />
      <GrantTierModal
        profile={profile}
        visible={isGrantTierModalOpen}
        setIsVisible={setIsGrantTierModalOpen}
      />
      <GrantPurchaseModal
        profile={profile}
        visible={showGrantPurchaseModal}
        onClose={() => setShowGrantPurchaseModal(false)}
      />
      <ClawbackPointsModal
        profile={profile}
        visible={showClawbackPointsModal}
        onClose={() => setShowClawbackPointsModal(false)}
      />
    </>
  )
}

export default ActionsMenu
